import React, { useEffect, useState } from "react";
import OrderSummarySection from "../orderSummary/OrderSummarySection";
import { useCurrency } from "../utility/CurrencyContext";

const CheckoutOrderSummary = ({
  cart,
  shippingAddress,
  convertPrice,
  caddress,
}) => {
  const [localCart, setLocalCart] = useState(() => {
    return JSON.parse(localStorage.getItem("cartItems")) || [];
  });
  const [localShipping, setLocalShipping] = useState(() => {
    return JSON.parse(localStorage.getItem("shippingAddress")) || {};
  });
  const { currency, getCurrencySymbol } = useCurrency();

  // Keep localCart in sync with context cart
  useEffect(() => {
    if (cart && cart.length > 0) {
      setLocalCart(cart);
    }
  }, [cart]);

  const displayCart = cart?.length > 0 ? cart : localCart;
  const displayShipping = shippingAddress || localShipping;

  // 5-tier pricing (matches Table2.js and Basket.js)
  const calculateItemPrice = (quantity, item) => {
    const qty = Number(quantity) || 1;

    let rawPrice;
    if (qty >= 10000 && item.price_10000plus) {
      rawPrice = item.price_10000plus;
    } else if (qty >= 5000 && item.price_5000_9999) {
      rawPrice = item.price_5000_9999;
    } else if (qty >= 1000 && item.price_1000_4999) {
      rawPrice = item.price_1000_4999;
    } else if (qty >= 250 && item.price_250_999) {
      rawPrice = item.price_250_999;
    } else if (item.price_1_249) {
      rawPrice = item.price_1_249;
    } else {
      rawPrice = 0;
    }

    const converted = convertPrice(Number(rawPrice) || 0);
    return converted / 100; // per-piece
  };

  const subtotal = displayCart.reduce((total, item) => {
    const unitPrice = calculateItemPrice(item.quantity, item);
    return total + unitPrice * item.quantity;
  }, 0);

  const vat = subtotal * 0.18;

  const minimumOrders = {
    RUPEE: 500,
    EURO: 5,
    DOLLAR: 6,
  };

  const requiredMinimum = minimumOrders[currency] ?? 6;
  const isBelowMinimum = subtotal < requiredMinimum;
  const minimumOrderAdjustment = isBelowMinimum
    ? requiredMinimum - subtotal
    : 0;
  const total = subtotal + vat + minimumOrderAdjustment;

  const hasShippingDetails =
    (displayShipping?.country && displayShipping.country !== "0") ||
    (displayShipping?.state && displayShipping.state !== "0") ||
    (displayShipping?.town && displayShipping.town !== "0");

  return (
    <form className="border border-gray-300 mt-5 bg-gray-50 p-6 shadow-sm">
      <div className="space-y-12">
        <div className="border-b border-gray-900/10 pb-12">
          <h1 className="text-2xl font-bold">Order Summary</h1>

          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-800 text-left">
              Your Items
            </h2>
            {displayCart.length > 0 ? (
              <table className="min-w-full mt-4">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 text-gray-700">Product</th>
                    <th className="text-right py-2 text-gray-700">
                      Unit Price
                    </th>
                    <th className="text-right py-2 text-gray-700">Quantity</th>
                    <th className="text-right py-2 text-gray-700">
                      Sub Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayCart.map((item) => {
                    const unit = calculateItemPrice(item.quantity, item);
                    const lineSubtotal = item.quantity * unit;
                    return (
                      <tr key={item.id} className="border-b border-gray-200">
                        <td className="py-3 text-gray-700">{item.name}</td>
                        <td className="py-3 text-right text-gray-700">
                          {getCurrencySymbol()} {unit.toFixed(2)}
                        </td>
                        <td className="py-3 text-right text-gray-700">
                          {item.quantity}
                        </td>
                        <td className="py-3 text-right text-gray-700">
                          {getCurrencySymbol()}
                          {lineSubtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="mt-2 text-gray-600 text-left">
                No items in your order yet.
              </p>
            )}
          </div>

          <OrderSummarySection
            getCurrencySymbol={getCurrencySymbol}
            subtotal={subtotal.toFixed(2)}
            vat={vat}
            minimumOrderAdjustment={minimumOrderAdjustment}
            total={total}
            requiredMinimum={requiredMinimum}
            hasShippingDetails={hasShippingDetails}
            displayShipping={displayShipping}
            shipAddress={caddress}
          />
        </div>
      </div>
    </form>
  );
};

export default CheckoutOrderSummary;